import * as t from 'io-ts';
import {useRef} from 'react';

import {useInitialEffect} from '../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {ApiType} from '../../libs/shared/core/src/models/api/commonApiTypes';
import {
  deleteApiAsync,
  fetchApiAsync,
  postApiAsync,
  putApiAsync,
} from '../../libs/shared/core/src/services/http/http';
import {delayAsync} from '../../src/helpers/asyncHelpers';

/*
 * Use Timeout.
 */

interface UseApiPublicOptions<T extends t.Type<any>> {
  apiType: ApiType<T>;
  debounceDelay?: number;
  onStateChange(state: UseApiState<t.TypeOf<T>>): void;
}

interface UseApiOptions<T extends t.Type<any>> extends UseApiPublicOptions<T> {
  method: 'get' | 'post' | 'put' | 'delete';
}

export type UseApiState<RESULT> = (
  | {type: 'loaded'; result: RESULT; error?: undefined}
  | {type: 'loading'; result?: undefined; error?: undefined}
  | {type: 'failed'; error: unknown; result?: undefined}
) & {asyncStatus: AsyncStatusesEnum};

/** @deprecated: Use useDebounce and fetchApiAsync instead. */
export function useFetchApi<T extends t.Type<any>>(options: UseApiPublicOptions<T>) {
  return useApi({method: 'get', ...options});
}

/** @deprecated: Use useDebounce and postApiAsync instead. */
export function usePostApi<T extends t.Type<any>>(options: UseApiPublicOptions<T>) {
  return useApi({method: 'post', ...options});
}

/** @deprecated: Use useDebounce and putApiAsync instead. */
export function usePutApi<T extends t.Type<any>>(options: UseApiPublicOptions<T>) {
  return useApi({method: 'put', ...options});
}

/** @deprecated: Use useDebounce and deleteApiAsync instead. */
export function useDeleteApi<T extends t.Type<any>>(options: UseApiPublicOptions<T>) {
  return useApi({method: 'delete', ...options});
}

/* Makes it easier to perform http calls within a React component, with support for debouncing and cancelling */
function useApi<T extends t.Type<any>>(options: UseApiOptions<T>) {
  const {apiType, method, debounceDelay, onStateChange} = options;

  const lastCancelTokenSource = useRef<CancelTokenSource>();

  function cancel() {
    if (lastCancelTokenSource.current) {
      lastCancelTokenSource.current.cancel();
    }
  }

  useInitialEffect(() => cancel);

  function apiCall(path: string, body?: unknown, contentType?: string) {
    cancel();
    onStateChange({type: 'loading', asyncStatus: AsyncStatusesEnum.LOADING});

    const cancelTokenSource = buildCancelTokenSource();
    lastCancelTokenSource.current = cancelTokenSource;

    const cancelToken = cancelTokenSource.token;

    const httpOptions = {
      cancelToken,
      headers: (contentType && {'content-type': contentType}) || undefined,
    };

    const delayPromise = debounceDelay ? delayAsync(debounceDelay, cancelToken) : Promise.resolve();

    return delayPromise
      .then(() => {
        switch (method) {
          case 'post':
            return postApiAsync(apiType, path, body, httpOptions);

          case 'put':
            return putApiAsync(apiType, path, body, httpOptions);

          case 'delete':
            return deleteApiAsync(apiType, path, body, httpOptions);

          default:
            return fetchApiAsync(apiType, path, httpOptions);
        }
      })
      .then((result) => {
        onStateChange({type: 'loaded', result, asyncStatus: AsyncStatusesEnum.LOADED});
        return result;
      })
      .catch((error) => {
        if (error instanceof CancelError) {
          return undefined;
        }

        onStateChange({type: 'failed', error, asyncStatus: AsyncStatusesEnum.FAILED});
        return undefined;
      });
  }

  return apiCall;
}
